// Pure trainer-link policy: how much of the trainer the app has right now, and
// what that allows. No Bluetooth, no React, no state.
//
// Three rungs form a ladder, each naming what the app still gets from the
// trainer:
//   ftms                read and drive
//   sensorsOnly         read
//   noTrainerConnected  nothing, and the session runs anyway, on the clock,
//                       the plan and whatever the heart rate belt sends
//
// `none` is not a rung on that ladder. It is the absence of a session, which
// is why it is the value an unrecognised link falls back to below.

export const TRAINER_LINK = Object.freeze({
  NONE: "none",
  NO_TRAINER: "noTrainerConnected",
  SENSORS_ONLY: "sensorsOnly",
  FTMS: "ftms",
});

// Anything unrecognised is NONE. A typo or a stale literal must not be able to
// start a session or drive a trainer, so the live predicates below fail closed.
function normalizeLink(link) {
  switch (link) {
    case TRAINER_LINK.NO_TRAINER:
    case TRAINER_LINK.SENSORS_ONLY:
    case TRAINER_LINK.FTMS:
      return link;
    default:
      return TRAINER_LINK.NONE;
  }
}

// The admin simulate flag stands in for a fully capable trainer: it feeds power,
// cadence and heart rate, and it accepts targets. Taking it as an argument here
// is what lets the 18 `isConnected || simulate` sites collapse into one call.
export function canRunSession({ link, simulate } = {}) {
  if (simulate) return true;
  return normalizeLink(link) !== TRAINER_LINK.NONE;
}

export function hasTrainerData({ link, simulate } = {}) {
  if (simulate) return true;
  const normalized = normalizeLink(link);
  return (
    normalized === TRAINER_LINK.SENSORS_ONLY || normalized === TRAINER_LINK.FTMS
  );
}

export function canDriveTrainer({ link, simulate } = {}) {
  if (simulate) return true;
  return normalizeLink(link) === TRAINER_LINK.FTMS;
}

// The two predicates below read saved history rows, not a live session, and they
// are deliberately asymmetric with the three above: only a session explicitly
// marked NO_TRAINER is held back. Every session saved before this field existed
// carries no link at all, and those must keep behaving exactly as they did.
//
// They also ignore `simulate` on purpose. How an admin's simulated session is
// treated by Strava and by the statistics is a separate question, unchanged here.
export function canUploadToStrava({ link } = {}) {
  return normalizeLink(link) !== TRAINER_LINK.NO_TRAINER;
}

export function countsInPowerStats({ link } = {}) {
  return normalizeLink(link) !== TRAINER_LINK.NO_TRAINER;
}
